import React, { useState, useRef, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { ContentCopy, Link, Description } from '@mui/icons-material';
import { StyledButton } from '../styles/StyledButtons';
import { StyledInput } from '../styles/StyledInputs';
import StyledModal from '../styles/StyledModal';
import { CloneMode } from 'BackendService';
import { StyledHeading } from '../styles/styles';
import CompactCardSelector, { SelectOption } from '../common/CompactCardSelector';
import FundOpportunity from '../../models/FundOpportunity';

interface CloneFundOpportunityModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (mode: CloneMode, newName: string) => void;
  isSubmitting?: boolean;
  modeError?: string;
  nameError?: string;
  fundOpportunity: FundOpportunity;
}

const CloneFundOpportunityModal: React.FC<CloneFundOpportunityModalProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  modeError,
  nameError,
  fundOpportunity,
}) => {
  const [mode, setMode] = useState<CloneMode | null>(null);
  const [newName, setNewName] = useState(`Copy of - ${fundOpportunity.name}`);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Focus on name input when modal opens
  useEffect(() => {
    if (open) {
      // Use setTimeout to ensure the modal is fully rendered
      const timer = setTimeout(() => {
        if (nameInputRef.current) {
          nameInputRef.current.focus();
          nameInputRef.current.select();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!mode) return;
    onSubmit(mode, newName);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setMode('clone-content');
      setNewName('');
      onClose();
    }
  };

  const isFormValid = mode && newName.trim();

  const cloneOptions: SelectOption[] = [
    {
      value: 'clone-content' as CloneMode,
      label: 'Clone with Content',
      description: 'Creates a complete copy with all content and images',
      icon: <ContentCopy />,
    },
    {
      value: 'link-content' as CloneMode,
      label: 'Link to Content',
      description:
        'Creates new fund opportunity that links to source content (edits to linked content affect all linked opportunities)',
      icon: <Link />,
    },
    {
      value: 'no-content-copy' as CloneMode,
      label: 'Metadata Only',
      description: 'Clones only metadata without content or linking',
      icon: <Description />,
    },
  ];

  return (
    <StyledModal
      fullWidth
      maxWidth="xl"
      open={open}
      onClose={handleClose}
      title="Clone Fund Opportunity"
      tone="tone1"
      disableClickAwayClose={isSubmitting}
      hideCloseButton={isSubmitting}
      actions={
        <Box display="flex" gap={2} justifyContent="center">
          <StyledButton
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
            buttonVariant="inactiveOrCancel"
          >
            Cancel
          </StyledButton>
          <StyledButton
            variant="contained"
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
          >
            {isSubmitting ? 'Cloning...' : 'Clone Opportunity'}
          </StyledButton>
        </Box>
      }
    >
      <Box display="flex" flexDirection="column" gap={4}>
        {/* New Name Input */}
        <Box>
          <StyledHeading headerVariant="subheading" gutterBottom>
            New Opportunity Name *
          </StyledHeading>
          <StyledInput
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Enter name for the new opportunity"
            error={!!nameError}
            helperText={nameError}
            disabled={isSubmitting}
            inputRef={nameInputRef}
          />
        </Box>

        {/* Clone Mode Selection */}
        <Box>
          <StyledHeading headerVariant="heading2" gutterBottom sx={{ textAlign: 'center' }}>
            Clone Mode
          </StyledHeading>
          <CompactCardSelector
            label="Clone Mode"
            options={cloneOptions}
            value={mode ?? ''}
            onChange={(value) => setMode(value as CloneMode)}
            error={modeError}
            cardSize="large"
          />
        </Box>
      </Box>
    </StyledModal>
  );
};

export default CloneFundOpportunityModal;
